import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Search, Loader2, PlayCircle, ChevronDown, ChevronUp,
  Globe, Code2, FileText, Link2, MessageSquare, Bot,
  TrendingUp, TrendingDown, Minus, Clock, CheckCircle2,
  XCircle, AlertTriangle, Zap, Shield, Eye, ArrowRight,
  RotateCcw, ExternalLink, Wrench, CircleDot, Trash2, X,
  BarChart3,
} from "lucide-react";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend,
} from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../../components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../../components/ui/dialog";

axios.defaults.withCredentials = true;
const API = process.env.REACT_APP_BACKEND_URL || "";

/* ─── Constants ─────────────────────────────────────────────── */

const CATEGORIES = [
  { key: "technical_seo", label: "Technical SEO", shortLabel: "Technical", weight: "25%", icon: Code2, accent: "#3b82f6" },
  { key: "on_page_seo", label: "On-Page SEO", shortLabel: "On-Page", weight: "25%", icon: FileText, accent: "#8b5cf6" },
  { key: "schema", label: "Schema / Structured Data", shortLabel: "Schema", weight: "15%", icon: Globe, accent: "#06b6d4" },
  { key: "off_page", label: "Off-Page Signals", shortLabel: "Off-Page", weight: "15%", icon: Link2, accent: "#f59e0b" },
  { key: "aeo", label: "Answer Engine (AEO)", shortLabel: "AEO", weight: "10%", icon: MessageSquare, accent: "#10b981" },
  { key: "geo", label: "Generative Engine (GEO)", shortLabel: "GEO", weight: "10%", icon: Bot, accent: "#ef4444" },
];

const CHART_COLORS = {
  overall: "#0f172a",
  technical_seo: "#3b82f6",
  on_page_seo: "#8b5cf6",
  schema: "#06b6d4",
  off_page: "#f59e0b",
  aeo: "#10b981",
  geo: "#ef4444",
};

const AUDIT_PHASES = [
  { emoji: "\ud83d\ude80", text: "Launching crawlers into the void..." },
  { emoji: "\ud83d\udd77\ufe0f", text: "Spider-bots crawling your pages..." },
  { emoji: "\ud83d\udcdd", text: "Reading every meta tag like a detective..." },
  { emoji: "\ud83e\udde0", text: "AI brain analyzing your schema markup..." },
  { emoji: "\ud83d\udd0d", text: "Inspecting robots.txt for hidden secrets..." },
  { emoji: "\ud83c\udf10", text: "Checking if AI bots can find you..." },
  { emoji: "\ud83d\udcca", text: "Crunching numbers like a caffeinated accountant..." },
  { emoji: "\ud83e\udd16", text: "Asking GPTBot if it knows who you are..." },
  { emoji: "\ud83c\udfa8", text: "Painting your score dashboard..." },
  { emoji: "\u2705", text: "Almost done! Generating your report..." },
];

const gradeColor = (g) => ({ A: "text-emerald-600", B: "text-blue-600", C: "text-yellow-600", D: "text-orange-600", F: "text-red-600" }[g] || "text-slate-500");
const gradeBg = (g) => ({ A: "bg-emerald-50 border-emerald-200", B: "bg-blue-50 border-blue-200", C: "bg-yellow-50 border-yellow-200", D: "bg-orange-50 border-orange-200", F: "bg-red-50 border-red-200" }[g] || "bg-slate-50 border-slate-200");
const scoreColor = (s) => s >= 90 ? "#10b981" : s >= 80 ? "#3b82f6" : s >= 70 ? "#eab308" : s >= 60 ? "#f97316" : "#ef4444";

const statusConfig = {
  pending: { label: "Pending", class: "bg-yellow-100 text-yellow-800 border-yellow-300", icon: Clock },
  running: { label: "Running", class: "bg-blue-100 text-blue-800 border-blue-300 animate-pulse", icon: Loader2 },
  completed: { label: "Complete", class: "bg-emerald-100 text-emerald-800 border-emerald-300", icon: CheckCircle2 },
  failed: { label: "Failed", class: "bg-red-100 text-red-800 border-red-300", icon: XCircle },
};

const priorityConfig = {
  critical: { class: "bg-red-600 text-white", icon: Zap },
  high: { class: "bg-orange-500 text-white", icon: AlertTriangle },
  medium: { class: "bg-yellow-400 text-yellow-900", icon: Shield },
  low: { class: "bg-slate-200 text-slate-700", icon: Eye },
};

/* ─── Score Ring ──────────────────────────────────────────────── */

function ScoreRing({ score, size = 120, strokeWidth = 8, color }) {
  const r = (size - strokeWidth) / 2;
  const c = 2 * Math.PI * r;
  return (
    <svg width={size} height={size} className="transform -rotate-90">
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#e2e8f0" strokeWidth={strokeWidth} />
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color || scoreColor(score)} strokeWidth={strokeWidth}
        strokeDasharray={c} strokeDashoffset={c - (score / 100) * c} strokeLinecap="round" className="transition-all duration-1000 ease-out" />
    </svg>
  );
}

function MiniGauge({ score, color, size = 56 }) {
  return (
    <div className="relative" style={{ width: size, height: size }}>
      <ScoreRing score={score} size={size} strokeWidth={5} color={color} />
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-xs font-bold font-mono" style={{ color }}>{score}</span>
      </div>
    </div>
  );
}

/* ─── Main Component ────────────────────────────────────────── */

export default function SeoAuditPage() {
  const [audits, setAudits] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [visibleLines, setVisibleLines] = useState({
    overall: true, technical_seo: true, on_page_seo: true,
    schema: true, off_page: true, aeo: true, geo: true,
  });
  const [fixingKey, setFixingKey] = useState(null);
  // Audit progress
  const [auditPhase, setAuditPhase] = useState(0);
  const [auditElapsed, setAuditElapsed] = useState(0);
  const auditStartTime = useRef(null);
  const phaseTimer = useRef(null);
  const elapsedTimer = useRef(null);
  // Detail modal
  const [modalAudit, setModalAudit] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  // Delete
  const [deletingId, setDeletingId] = useState(null);

  const pollRef = useRef(null);

  /* ─── Data Fetching ───────────────────────────────────────── */

  const fetchAudits = useCallback(async () => {
    try {
      const [ar, hr] = await Promise.all([
        axios.get(`${API}/api/seo-audit/audits`),
        axios.get(`${API}/api/seo-audit/scores/history`),
      ]);
      setAudits(ar.data.audits || []);
      setHistory(hr.data.history || []);
    } catch (err) {
      console.error("Failed to fetch audits:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAudits(); }, [fetchAudits]);

  /* ─── Polling ─────────────────────────────────────────────── */

  const wasRunningRef = useRef(false);

  const clearTimers = useCallback(() => {
    if (phaseTimer.current) { clearInterval(phaseTimer.current); phaseTimer.current = null; }
    if (elapsedTimer.current) { clearInterval(elapsedTimer.current); elapsedTimer.current = null; }
  }, []);

  const hasRunningAudit = audits.some((a) => a.status === "pending" || a.status === "running");

  useEffect(() => {
    if (hasRunningAudit && !pollRef.current) {
      pollRef.current = setInterval(() => fetchAudits(), 5000);
      wasRunningRef.current = true;
    } else if (!hasRunningAudit && pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    // Detect completion: was running, now all done — clear timers + notify
    if (!hasRunningAudit && wasRunningRef.current) {
      wasRunningRef.current = false;
      clearTimers();
      const justCompleted = audits.find((a) => a.status === "completed");
      if (justCompleted) {
        const grade = justCompleted.grade || "?";
        const score = justCompleted.scores?.overall ?? "?";
        toast.success(`Audit complete — Score: ${score} (${grade})`, { duration: 8000 });
      }
      setAuditElapsed(0);
      setAuditPhase(0);
    }
    return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [audits, fetchAudits, clearTimers]);

  // Frontend safety: force-kill progress after 6 minutes (backend timeout is 5 min)
  useEffect(() => {
    if (auditElapsed > 360 && (phaseTimer.current || elapsedTimer.current)) {
      clearTimers();
      fetchAudits();
    }
  }, [auditElapsed, clearTimers, fetchAudits]);

  // Safety: force-clear stuck progress after 5 minutes
  useEffect(() => {
    if (!hasRunningAudit) return;
    const safetyTimer = setInterval(() => {
      if (!auditStartTime.current) return;
      const elapsed = Date.now() - auditStartTime.current;
      if (elapsed > 5 * 60 * 1000) {
        if (phaseTimer.current) { clearInterval(phaseTimer.current); phaseTimer.current = null; }
        if (elapsedTimer.current) { clearInterval(elapsedTimer.current); elapsedTimer.current = null; }
        auditStartTime.current = null;
        setAuditElapsed(0);
        setAuditPhase(0);
        toast.warning("Audit timed out after 5 minutes. Check status below.", { duration: 8000 });
        fetchAudits();
        clearInterval(safetyTimer);
      }
    }, 10000);
    return () => clearInterval(safetyTimer);
  }, [hasRunningAudit, fetchAudits]);

  /* ─── Start Audit ─────────────────────────────────────────── */

  const startAudit = async () => {
    setStarting(true);
    setAuditPhase(0);
    setAuditElapsed(0);
    auditStartTime.current = Date.now();
    wasRunningRef.current = true;
    try {
      const { data } = await axios.post(`${API}/api/seo-audit/start`, { domain: "jobtalk.ai" });
      toast.success("Audit started for jobtalk.ai");
      setAudits((prev) => [data.audit, ...prev]);
      // Start phase rotation
      phaseTimer.current = setInterval(() => {
        setAuditPhase((p) => (p + 1) % AUDIT_PHASES.length);
      }, 4000);
      // Start elapsed timer
      elapsedTimer.current = setInterval(() => {
        setAuditElapsed(Math.floor((Date.now() - auditStartTime.current) / 1000));
      }, 1000);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to start audit");
    } finally {
      setStarting(false);
    }
  };

  /* ─── Cancel Audit ────────────────────────────────────────── */

  const cancelAudit = async () => {
    const active = audits.find((a) => a.status === "pending" || a.status === "running");
    if (!active) return;
    try {
      await axios.post(`${API}/api/seo-audit/cancel/${active.id}`);
      toast.info("Audit cancelled");
      clearTimers();
      setAuditElapsed(0);
      fetchAudits();
    } catch (err) {
      toast.error("Failed to cancel audit");
    }
  };

  /* ─── Delete Audit ────────────────────────────────────────── */

  const deleteAudit = async (auditId) => {
    setDeletingId(auditId);
    try {
      await axios.delete(`${API}/api/seo-audit/audits/${auditId}`);
      toast.success("Audit deleted");
      setAudits((prev) => prev.filter((a) => a.id !== auditId));
      if (modalAudit?.id === auditId) setModalAudit(null);
      fetchAudits();
    } catch (err) {
      toast.error("Failed to delete audit");
    } finally {
      setDeletingId(null);
    }
  };

  /* ─── Open Detail Modal ───────────────────────────────────── */

  const openModal = async (auditId) => {
    setModalLoading(true);
    setModalAudit(null);
    try {
      const { data } = await axios.get(`${API}/api/seo-audit/audits/${auditId}`);
      setModalAudit(data.audit || data);
    } catch {
      toast.error("Failed to load audit details");
    } finally {
      setModalLoading(false);
    }
  };

  /* ─── Request Fix ─────────────────────────────────────────── */

  const requestFix = async (auditId, fixKey, title) => {
    setFixingKey(fixKey);
    try {
      await axios.post(`${API}/api/seo-audit/fix`, { audit_id: auditId, fix_key: fixKey });
      toast.success(`Fix requested: ${title}`, { description: "Queued for jobtalk.ai codebase." });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to request fix");
    } finally {
      setFixingKey(null);
    }
  };

  /* ─── Chart Legend Click ──────────────────────────────────── */

  const handleLegendClick = (entry) => {
    setVisibleLines((prev) => ({ ...prev, [entry.dataKey]: !prev[entry.dataKey] }));
  };

  /* ─── Derived State ───────────────────────────────────────── */

  const latest = audits.find((a) => a.status === "completed");
  const isRunning = audits.some((a) => a.status === "pending" || a.status === "running");
  const prevCompleted = audits.filter((a) => a.status === "completed")[1];
  const getDelta = (key) => {
    if (!latest?.scores || !prevCompleted?.scores) return null;
    return latest.scores[key] - prevCompleted.scores[key];
  };

  const chartData = [...history].reverse().map((h) => ({
    date: new Date(h.date).toLocaleDateString("en-US", { month: "short", day: "numeric" }),
    overall: h.overall, technical_seo: h.technical_seo, on_page_seo: h.on_page_seo,
    schema: h.schema, off_page: h.off_page, aeo: h.aeo, geo: h.geo,
  }));

  // Real progress from backend crawl, fall back to time-based estimate
  const runningAudit = audits.find((a) => a.status === "running" || a.status === "pending");
  const realCrawled = runningAudit?.pages_crawled || 0;
  const realTotal = runningAudit?.pages_total || 300;
  const realPct = realCrawled > 0 ? Math.min(95, Math.round((realCrawled / realTotal) * 100)) : 0;
  const estimatedPct = realPct > 0 ? realPct : Math.min(95, Math.round((auditElapsed / 60) * 100));

  const formatElapsed = (s) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return m > 0 ? `${m}m ${sec}s` : `${sec}s`;
  };

  /* ─── Custom Chart Tooltip ────────────────────────────────── */

  const ChartTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="bg-white/95 backdrop-blur border border-slate-200 rounded-lg shadow-xl p-3 min-w-[180px]">
        <p className="text-xs font-semibold text-slate-500 mb-2 uppercase tracking-wider">{label}</p>
        {payload.map((p) => (
          <div key={p.dataKey} className="flex items-center justify-between gap-4 py-0.5">
            <div className="flex items-center gap-2">
              <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: p.color }} />
              <span className="text-xs text-slate-600">{p.dataKey === "overall" ? "Overall" : CATEGORIES.find((c) => c.key === p.dataKey)?.shortLabel || p.dataKey}</span>
            </div>
            <span className="text-xs font-bold font-mono" style={{ color: p.color }}>{p.value}</span>
          </div>
        ))}
      </div>
    );
  };

  const renderLegend = ({ payload }) => (
    <div className="flex flex-wrap justify-center gap-x-4 gap-y-1 mt-2">
      {payload.map((entry) => (
        <button key={entry.dataKey} onClick={() => handleLegendClick(entry)}
          className={`flex items-center gap-1.5 text-xs px-2 py-1 rounded-md transition-all ${visibleLines[entry.dataKey] ? "opacity-100 bg-slate-50" : "opacity-40 line-through"} hover:opacity-80`}>
          <div className="w-3 h-1 rounded-full" style={{ backgroundColor: entry.color }} />
          <span>{entry.dataKey === "overall" ? "Overall" : CATEGORIES.find((c) => c.key === entry.dataKey)?.shortLabel || entry.dataKey}</span>
        </button>
      ))}
    </div>
  );

  /* ─── Loading ─────────────────────────────────────────────── */

  if (loading) return <div className="flex items-center justify-center py-32"><Loader2 className="h-8 w-8 animate-spin text-blue-600" /></div>;

  /* ─── Empty State ─────────────────────────────────────────── */

  if (audits.length === 0 && !starting) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="text-center py-20 border-2 border-dashed border-slate-200 rounded-2xl bg-gradient-to-b from-slate-50/80 to-white">
          <div className="relative inline-block mb-6">
            <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-blue-500 to-violet-600 flex items-center justify-center shadow-lg shadow-blue-500/25">
              <Search className="h-10 w-10 text-white" />
            </div>
            <div className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-emerald-500 flex items-center justify-center border-2 border-white">
              <TrendingUp className="h-3.5 w-3.5 text-white" />
            </div>
          </div>
          <h2 className="text-2xl font-bold text-slate-800 mb-2">SEO &middot; AEO &middot; GEO Audit</h2>
          <p className="text-slate-500 mb-1 max-w-md mx-auto">Analyze your site across 6 dimensions &mdash; Technical SEO, On-Page, Schema, Off-Page, Answer Engine, and Generative Engine optimization.</p>
          <p className="text-xs text-slate-400 mb-8">Target: <span className="font-mono font-medium text-slate-600">jobtalk.ai</span></p>
          <Button onClick={startAudit} disabled={starting}
            className="bg-gradient-to-r from-blue-600 to-violet-600 hover:from-blue-700 hover:to-violet-700 text-white px-8 py-3 text-base shadow-lg shadow-blue-500/25">
            {starting ? <><Loader2 className="h-5 w-5 animate-spin mr-2" /> Starting...</> : <><PlayCircle className="h-5 w-5 mr-2" /> Run First Audit</>}
          </Button>
        </div>
      </div>
    );
  }

  /* ─── Main Dashboard ──────────────────────────────────────── */

  return (
    <div className="max-w-[1400px] mx-auto px-4 py-6 space-y-6">

      {/* ── Header ─────────────────────────────────────────── */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 tracking-tight">SEO &middot; AEO &middot; GEO</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Audit dashboard for <span className="font-mono font-medium text-slate-700">jobtalk.ai</span>
            {latest && <span className="ml-2 text-slate-400">&middot; Last run {new Date(latest.completed_at || latest.started_at).toLocaleDateString()}</span>}
          </p>
        </div>
        <Button onClick={startAudit} disabled={starting || isRunning}
          className="bg-gradient-to-r from-blue-600 to-violet-600 hover:from-blue-700 hover:to-violet-700 text-white shadow-md">
          {starting || isRunning ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Running...</> : <><RotateCcw className="h-4 w-4 mr-2" /> New Audit</>}
        </Button>
      </div>

      {/* ── Running Progress Banner ────────────────────────── */}
      {isRunning && (
        <Card className="border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50 overflow-hidden">
          <CardContent className="p-5">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="text-3xl animate-bounce">{AUDIT_PHASES[auditPhase].emoji}</div>
                <div>
                  <p className="text-sm font-semibold text-blue-900">{AUDIT_PHASES[auditPhase].text}</p>
                  <p className="text-xs text-blue-600 mt-0.5">Crawling jobtalk.ai &middot; {formatElapsed(auditElapsed)} elapsed</p>
                </div>
              </div>
              <Button variant="outline" size="sm" onClick={cancelAudit}
                className="text-xs border-red-200 text-red-600 hover:bg-red-50 hover:border-red-300">
                <X className="h-3 w-3 mr-1" /> Cancel
              </Button>
            </div>
            {/* Progress bar */}
            <div className="w-full h-2 bg-blue-200/50 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-blue-500 to-violet-500 rounded-full transition-all duration-1000 ease-out"
                style={{ width: `${estimatedPct}%` }} />
            </div>
            <div className="flex justify-between mt-1.5">
              <span className="text-[10px] font-mono text-blue-500">{estimatedPct}%</span>
              <span className="text-[10px] text-blue-400">{realCrawled > 0 ? `${realCrawled}/${realTotal} pages crawled` : "~60s for full crawl (300 pages)"}</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── Score Dashboard ────────────────────────────────── */}
      {latest && (
        <div className="grid grid-cols-12 gap-4">
          <Card className="col-span-12 lg:col-span-3 border-0 shadow-md bg-gradient-to-br from-slate-800 to-slate-900 text-white overflow-hidden">
            <CardContent className="p-6 flex flex-col items-center justify-center relative">
              <div className="absolute top-3 right-3">
                <span className={`text-5xl font-black font-mono ${latest.grade === "A" ? "text-emerald-400" : latest.grade === "B" ? "text-blue-400" : latest.grade === "C" ? "text-yellow-400" : latest.grade === "D" ? "text-orange-400" : "text-red-400"}`}>
                  {latest.grade}
                </span>
              </div>
              <div className="relative mb-3">
                <ScoreRing score={latest.scores.overall} size={140} strokeWidth={10} />
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-4xl font-black font-mono tracking-tight text-white">{latest.scores.overall}</span>
                </div>
              </div>
              <p className="text-sm font-medium text-slate-300 uppercase tracking-widest">Overall Score</p>
              {getDelta("overall") !== null && (
                <div className={`flex items-center gap-1 mt-2 text-sm font-mono ${getDelta("overall") > 0 ? "text-emerald-400" : getDelta("overall") < 0 ? "text-red-400" : "text-slate-400"}`}>
                  {getDelta("overall") > 0 ? <TrendingUp className="h-3.5 w-3.5" /> : getDelta("overall") < 0 ? <TrendingDown className="h-3.5 w-3.5" /> : <Minus className="h-3.5 w-3.5" />}
                  <span>{getDelta("overall") > 0 ? "+" : ""}{getDelta("overall")} pts</span>
                </div>
              )}
            </CardContent>
          </Card>
          <div className="col-span-12 lg:col-span-9 grid grid-cols-2 md:grid-cols-3 gap-3">
            {CATEGORIES.map((cat) => {
              const score = latest.scores?.[cat.key] ?? 0;
              const delta = getDelta(cat.key);
              const Icon = cat.icon;
              return (
                <Card key={cat.key} className="border shadow-sm hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: cat.accent + "15" }}>
                          <Icon className="h-4 w-4" style={{ color: cat.accent }} />
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-slate-800">{cat.shortLabel}</p>
                          <p className="text-[10px] text-slate-400 font-mono">{cat.weight} weight</p>
                        </div>
                      </div>
                      <MiniGauge score={score} color={cat.accent} />
                    </div>
                    <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full rounded-full transition-all duration-1000 ease-out" style={{ width: `${score}%`, backgroundColor: cat.accent }} />
                    </div>
                    {delta !== null && (
                      <div className={`flex items-center gap-1 mt-2 text-xs font-mono ${delta > 0 ? "text-emerald-600" : delta < 0 ? "text-red-500" : "text-slate-400"}`}>
                        {delta > 0 ? <TrendingUp className="h-3 w-3" /> : delta < 0 ? <TrendingDown className="h-3 w-3" /> : <Minus className="h-3 w-3" />}
                        <span>{delta > 0 ? "+" : ""}{delta} from last</span>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Score Trend Chart ──────────────────────────────── */}
      {chartData.length > 1 && (
        <Card className="border shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold text-slate-700 flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-blue-500" /> Score Trends
            </CardTitle>
          </CardHeader>
          <CardContent className="pb-4">
            <ResponsiveContainer width="100%" height={320}>
              <LineChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={{ stroke: "#e2e8f0" }} tickLine={false} />
                <YAxis domain={[0, 100]} tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={{ stroke: "#e2e8f0" }} tickLine={false} />
                <Tooltip content={<ChartTooltip />} />
                <Legend content={renderLegend} />
                <Line type="monotone" dataKey="overall" name="Overall" stroke={CHART_COLORS.overall} strokeWidth={3} dot={{ r: 4, fill: CHART_COLORS.overall }} activeDot={{ r: 6 }} hide={!visibleLines.overall} />
                {CATEGORIES.map((cat) => (
                  <Line key={cat.key} type="monotone" dataKey={cat.key} name={cat.shortLabel} stroke={cat.accent} strokeWidth={1.5}
                    dot={{ r: 3, fill: cat.accent }} activeDot={{ r: 5 }} strokeDasharray={cat.key === "aeo" || cat.key === "geo" ? "4 2" : undefined} hide={!visibleLines[cat.key]} />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}
      {chartData.length === 1 && (
        <Card className="border shadow-sm"><CardContent className="py-8 text-center">
          <TrendingUp className="h-8 w-8 text-slate-300 mx-auto mb-3" />
          <p className="text-sm text-slate-500">Run more audits to see score trends over time.</p>
        </CardContent></Card>
      )}

      {/* ── Action Items / Recommendations ────────────────── */}
      {latest?.action_items?.length > 0 && (
        <Card className="border shadow-sm">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base font-semibold text-slate-700 flex items-center gap-2">
                <Wrench className="h-4 w-4 text-orange-500" /> Recommendations
                <Badge variant="secondary" className="ml-2 font-mono text-xs">{latest.action_items.length}</Badge>
              </CardTitle>
              <div className="flex items-center gap-3 text-xs text-slate-400">
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500" /> Critical</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-orange-500" /> High</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-yellow-400" /> Medium</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-slate-300" /> Low</span>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-2">
            {latest.action_items.map((item, idx) => {
              const pri = priorityConfig[item.priority] || priorityConfig.low;
              const PriIcon = pri.icon;
              const catInfo = CATEGORIES.find((c) => c.key === item.category) || {};
              const CatIcon = catInfo.icon || CircleDot;
              return (
                <div key={idx} className="flex items-start gap-3 p-3 rounded-lg border border-slate-100 bg-white hover:border-slate-200 hover:bg-slate-50/50 transition-all group">
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold uppercase ${pri.class} flex-shrink-0 mt-0.5`}>
                    <PriIcon className="h-2.5 w-2.5" />{item.priority}
                  </span>
                  <div className="w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0" style={{ backgroundColor: (catInfo.accent || "#94a3b8") + "15" }}>
                    <CatIcon className="h-3.5 w-3.5" style={{ color: catInfo.accent || "#94a3b8" }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-800">{item.title}</p>
                    <p className="text-xs text-slate-400 mt-0.5">{item.description}</p>
                  </div>
                  {item.fix_key ? (
                    <Button variant="outline" size="sm" disabled={fixingKey === item.fix_key}
                      onClick={() => requestFix(latest.id, item.fix_key, item.title)}
                      className="flex-shrink-0 text-xs border-blue-200 text-blue-600 hover:bg-blue-50 hover:border-blue-300 opacity-0 group-hover:opacity-100 transition-opacity">
                      {fixingKey === item.fix_key ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Wrench className="h-3 w-3 mr-1" />} Fix It
                    </Button>
                  ) : (
                    <span className="flex-shrink-0 text-[10px] font-mono text-slate-300 uppercase mt-1">manual</span>
                  )}
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}

      {/* ── Audit History Table ────────────────────────────── */}
      <Card className="border shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-semibold text-slate-700 flex items-center gap-2">
            <Clock className="h-4 w-4 text-slate-400" /> Audit History
            <Badge variant="secondary" className="ml-2 font-mono text-xs">{audits.length}</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50/80 hover:bg-slate-50/80">
                <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500 pl-6">Date</TableHead>
                <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Domain</TableHead>
                <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500 text-center">Score</TableHead>
                <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500 text-center">Grade</TableHead>
                <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500 text-center">Status</TableHead>
                <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500 text-right pr-6">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {audits.map((audit) => {
                const sc = statusConfig[audit.status] || statusConfig.pending;
                const StatusIcon = sc.icon;
                return (
                  <TableRow key={audit.id} className="hover:bg-slate-50/60 group">
                    <TableCell className="pl-6 font-mono text-sm text-slate-600">
                      {new Date(audit.started_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                      <span className="block text-[10px] text-slate-400">
                        {new Date(audit.started_at).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" })}
                      </span>
                    </TableCell>
                    <TableCell><span className="font-mono text-sm text-slate-700">{audit.domain}</span></TableCell>
                    <TableCell className="text-center">
                      {audit.status === "completed" ? (
                        <span className="text-lg font-black font-mono" style={{ color: scoreColor(audit.scores?.overall || 0) }}>{audit.scores?.overall ?? "\u2014"}</span>
                      ) : <span className="text-sm text-slate-400">\u2014</span>}
                    </TableCell>
                    <TableCell className="text-center">
                      {audit.status === "completed" && audit.grade ? (
                        <span className={`inline-flex items-center justify-center w-9 h-9 rounded-lg border-2 text-lg font-black font-mono ${gradeBg(audit.grade)} ${gradeColor(audit.grade)}`}>{audit.grade}</span>
                      ) : <span className="text-sm text-slate-400">\u2014</span>}
                    </TableCell>
                    <TableCell className="text-center">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${sc.class}`}>
                        <StatusIcon className={`h-3 w-3 ${audit.status === "running" ? "animate-spin" : ""}`} />{sc.label}
                      </span>
                    </TableCell>
                    <TableCell className="text-right pr-6">
                      <div className="flex items-center justify-end gap-1">
                        {audit.status === "completed" && (
                          <Button variant="ghost" size="sm" onClick={() => openModal(audit.id)} className="text-xs text-slate-500 hover:text-blue-600">
                            <BarChart3 className="h-3.5 w-3.5 mr-1" /> View
                          </Button>
                        )}
                        <Button variant="ghost" size="sm" onClick={() => deleteAudit(audit.id)}
                          disabled={deletingId === audit.id}
                          className="text-xs text-slate-300 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity">
                          {deletingId === audit.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* ── Full Detail Modal ──────────────────────────────── */}
      <Dialog open={!!modalAudit || modalLoading} onOpenChange={(open) => { if (!open) { setModalAudit(null); setModalLoading(false); } }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          {modalLoading ? (
            <div className="flex items-center justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-blue-600" /></div>
          ) : modalAudit && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-3">
                  <span className="text-xl font-bold">Audit Report</span>
                  <span className="font-mono text-sm text-slate-500">{modalAudit.domain}</span>
                  {modalAudit.grade && (
                    <span className={`inline-flex items-center justify-center w-10 h-10 rounded-xl border-2 text-xl font-black font-mono ${gradeBg(modalAudit.grade)} ${gradeColor(modalAudit.grade)}`}>
                      {modalAudit.grade}
                    </span>
                  )}
                </DialogTitle>
                <DialogDescription>
                  {new Date(modalAudit.started_at).toLocaleString()} &middot; {modalAudit.kpis?.pages_crawled || 0} pages crawled &middot; {modalAudit.kpis?.pages_with_errors || 0} errors
                </DialogDescription>
              </DialogHeader>

              {/* Overall Score */}
              <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-xl my-4">
                <div className="relative">
                  <ScoreRing score={modalAudit.scores?.overall || 0} size={80} strokeWidth={7} />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-xl font-black font-mono">{modalAudit.scores?.overall}</span>
                  </div>
                </div>
                <div>
                  <p className="text-lg font-bold text-slate-800">Overall Score: {modalAudit.scores?.overall}/100</p>
                  <p className="text-sm text-slate-500">Weighted average across 6 categories</p>
                </div>
              </div>

              {/* Category Sections */}
              <div className="space-y-4">
                {CATEGORIES.map((cat) => {
                  const score = modalAudit.scores?.[cat.key] ?? 0;
                  const kpis = modalAudit.kpis?.[cat.key.replace("_seo", "").replace("on_page", "on_page")] || modalAudit.kpis?.[cat.key] || {};
                  const Icon = cat.icon;

                  // Map category key to KPI key
                  let kpiKey = cat.key;
                  if (cat.key === "technical_seo") kpiKey = "technical";
                  else if (cat.key === "on_page_seo") kpiKey = "on_page";
                  const kpiData = modalAudit.kpis?.[kpiKey] || {};

                  return (
                    <div key={cat.key} className="border rounded-xl p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: cat.accent + "15" }}>
                            <Icon className="h-4 w-4" style={{ color: cat.accent }} />
                          </div>
                          <div>
                            <p className="text-sm font-bold text-slate-800">{cat.label}</p>
                            <p className="text-[10px] text-slate-400 font-mono">{cat.weight} of overall</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-2xl font-black font-mono" style={{ color: cat.accent }}>{score}</span>
                          <span className="text-xs text-slate-400">/100</span>
                        </div>
                      </div>
                      <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden mb-3">
                        <div className="h-full rounded-full" style={{ width: `${score}%`, backgroundColor: cat.accent }} />
                      </div>
                      {/* KPI details */}
                      {Object.keys(kpiData).length > 0 && (
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                          {Object.entries(kpiData).map(([k, v]) => {
                            const display = typeof v === "boolean" ? (v ? "\u2705" : "\u274c") : Array.isArray(v) ? v.join(", ") || "None" : String(v);
                            return (
                              <div key={k} className="bg-slate-50 rounded-lg px-3 py-2">
                                <p className="text-[10px] text-slate-400 font-mono uppercase truncate">{k.replace(/_/g, " ")}</p>
                                <p className="text-sm font-medium text-slate-700 truncate" title={display}>{display}</p>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Action Items */}
              {modalAudit.action_items?.length > 0 && (
                <div className="mt-6">
                  <h3 className="text-sm font-bold text-slate-700 mb-3 flex items-center gap-2">
                    <Wrench className="h-4 w-4 text-orange-500" /> Fixes Needed ({modalAudit.action_items.length})
                  </h3>
                  <div className="space-y-2">
                    {modalAudit.action_items.map((item, idx) => {
                      const pri = priorityConfig[item.priority] || priorityConfig.low;
                      const PriIcon = pri.icon;
                      return (
                        <div key={idx} className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold uppercase ${pri.class} flex-shrink-0`}>
                            <PriIcon className="h-2.5 w-2.5" />{item.priority}
                          </span>
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-slate-800">{item.title}</p>
                            <p className="text-xs text-slate-500 mt-0.5">{item.description}</p>
                          </div>
                          <span className="text-[10px] font-mono text-slate-400 flex-shrink-0 uppercase">{item.category}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* No issues */}
              {(!modalAudit.action_items || modalAudit.action_items.length === 0) && (
                <div className="text-center py-6 text-slate-400">
                  <CheckCircle2 className="h-8 w-8 mx-auto mb-2 text-emerald-400" />
                  <p className="text-sm">No issues found! Your site is looking great.</p>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
